import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  SlidersHorizontal,
  BadgeCheck,
  Heart,
  Mail,
  ScanLine,
} from "lucide-react";

const LAPTOP =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/0yj2rcb6_rarefig.jpg";
const FULLPAGE =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/92z8xhji_Untitled-2.jpg";
const LOGO =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/1kb4331t_image.png";
const LOCKUP =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/8ejpaalx_image.png";
const SHOP_SCENE =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/9uyvne5e_image.png";
const SCANNER =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/rmmja30r_image.png";
const SCAN_VIDEO =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/8s6n8tcq_scan.mp4";
const BENDER =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/685vbep5_image.png";
const BATMAN =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/ug9z09zg_image.png";
const NARUTO =
  "https://customer-assets.emergentagent.com/job_portfolio-pro-1623/artifacts/6ml63uys_image.png";

const palette = [
  { name: "Dragon Yellow", hex: "#FFD23F", text: "#14366B" },
  { name: "Comic Sky", hex: "#56B7CE", text: "#FFF6E3" },
  { name: "Pulp Cream", hex: "#FFF6E3", text: "#14366B" },
  { name: "Inked Navy", hex: "#14366B", text: "#FFF6E3" },
  { name: "Kapow Red", hex: "#C9342B", text: "#FFF6E3" },
];

const features = [
  {
    icon: SlidersHorizontal,
    title: "Layered Filters",
    body: "Collectors hunt with precision: stacked filters for product type, theme and price band narrow thousands of figures down to the exact grail in seconds.",
    bg: "#56B7CE",
    iconColor: "#FFF6E3",
  },
  {
    icon: BadgeCheck,
    title: "Grading Service",
    body: "A dedicated grading flow lets collectors send in figures for authentication and condition scoring — the trust layer that separates Rarefig from marketplace noise.",
    bg: "#FFD23F",
    iconColor: "#14366B",
  },
  {
    icon: Heart,
    title: "Wishlist & Badges",
    body: "Corner ribbons shout WEB EXCLUSIVE and NEW, while wishlist hearts keep every collector's hit-list one tap away.",
    bg: "#C9342B",
    iconColor: "#FFF6E3",
  },
  {
    icon: Mail,
    title: "Newsletter Drops",
    body: "The mascot dragon delivers release news straight to inboxes — new arrivals, restocks and events before they sell out.",
    bg: "#14366B",
    iconColor: "#FFD23F",
  },
];

const brickColors = ["#FFD23F", "#C9342B", "#56B7CE", "#14366B"];

function LegoDivider() {
  return (
    <div
      className="flex justify-center gap-1 py-8 flex-wrap px-4"
      data-testid="rarefig-lego-divider"
      aria-hidden="true"
    >
      {Array.from({ length: 10 }).map((_, i) => (
        <motion.div
          key={i}
          whileHover={{ y: -8, rotate: i % 2 ? 4 : -4 }}
          whileTap={{ scale: 0.85 }}
          className="relative mt-3 w-14 h-7 border-4 border-[#14366B] cursor-pointer"
          style={{ background: brickColors[i % 4] }}
        >
          {[2, 28].map((left) => (
            <span
              key={left}
              className="absolute -top-[11px] w-4 h-[11px] border-4 border-b-0 border-[#14366B]"
              style={{ background: brickColors[i % 4], left }}
            />
          ))}
        </motion.div>
      ))}
    </div>
  );
}

const demoFigures = [
  {
    id: "bender",
    label: "Bender — Futurama (MIB)",
    img: BENDER,
    color: "#56B7CE",
    grade: "9.2",
    cond: "MINT IN BOX",
    note: "Crisp blister, vibrant cardback, Bender stands tall. Shelf-ready.",
  },
  {
    id: "batman",
    label: "Retro-Action Batman — DC",
    img: BATMAN,
    color: "#FFD23F",
    grade: "8.7",
    cond: "EXCELLENT",
    note: "Pink cardback only kissed at the corner. Cape and cowl pristine.",
  },
  {
    id: "naruto",
    label: "Naruto Six Paths — Funko Pop!",
    img: NARUTO,
    color: "#C9342B",
    grade: "9.5",
    cond: "GEM MINT",
    note: "Paint is razor-sharp, chakra aura unscuffed. A grail in glow-up form.",
  },
];

function GradingDemo() {
  const [picked, setPicked] = useState(null);
  const [phase, setPhase] = useState("pick"); // pick | scanning | result

  const startScan = () => {
    setPhase("scanning");
    setTimeout(() => setPhase("result"), 2000);
  };
  const reset = () => {
    setPicked(null);
    setPhase("pick");
  };
  const fig = demoFigures.find((f) => f.id === picked);

  return (
    <div
      className="relative max-w-[760px] mx-auto bg-[#FFF6E3] border-4 border-[#14366B] shadow-[12px_12px_0_#14366B] p-8 md:p-12"
      data-testid="rarefig-grading-demo"
    >
      <div className="absolute -top-5 left-8 px-4 py-1.5 bg-[#C9342B] text-[#FFF6E3] text-xs font-extrabold uppercase tracking-[0.2em]">
        Grade-o-Matic 3000
      </div>
      <AnimatePresence mode="wait">
        {phase === "pick" && (
          <motion.div
            key="pick"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <h3 className="text-2xl md:text-3xl font-extrabold uppercase tracking-wide">
              Pick a collectible to grade
            </h3>
            <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {demoFigures.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setPicked(f.id)}
                  data-testid={`grade-fig-${f.id}`}
                  className={`border-4 p-6 flex flex-col items-center gap-3 transition-all duration-300 hover:-translate-y-1 ${
                    picked === f.id
                      ? "border-[#C9342B] bg-[#FFD23F] shadow-[6px_6px_0_#C9342B]"
                      : "border-[#14366B] bg-white shadow-[6px_6px_0_#14366B]"
                  }`}
                >
                  <span
                    className="w-24 h-24 border-4 border-[#14366B] flex items-center justify-center overflow-hidden"
                    style={{ background: f.color }}
                  >
                    <img
                      src={f.img}
                      alt={f.label}
                      className="w-full h-full object-contain"
                      loading="lazy"
                    />
                  </span>
                  <span className="text-xs font-extrabold uppercase tracking-wide text-center leading-snug">
                    {f.label}
                  </span>
                </button>
              ))}
            </div>
            <button
              disabled={!picked}
              onClick={startScan}
              data-testid="grade-scan-btn"
              className="mt-8 w-full py-4 bg-[#C9342B] text-[#FFF6E3] text-lg font-extrabold uppercase tracking-[0.2em] border-4 border-[#14366B] shadow-[6px_6px_0_#14366B] hover:translate-y-[2px] hover:shadow-[4px_4px_0_#14366B] transition-all disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              <ScanLine className="w-6 h-6" /> Scan it!
            </button>
          </motion.div>
        )}
        {phase === "scanning" && (
          <motion.div
            key="scan"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="text-center py-6"
            data-testid="grade-scanning"
          >
            <div
              className="relative mx-auto w-48 h-48 border-4 border-[#14366B] flex items-center justify-center overflow-hidden"
              style={{ background: fig.color }}
            >
              <img
                src={fig.img}
                alt={fig.label}
                className="w-full h-full object-contain"
              />
              <span className="rarefig-scanline" />
            </div>
            <p className="mt-6 text-xl font-extrabold uppercase tracking-[0.3em] text-[#14366B] animate-pulse">
              Scanning…
            </p>
            <p className="mt-2 text-sm font-bold uppercase tracking-wide text-[#3a5384]">
              Checking paint, joints, box corners & vibes
            </p>
          </motion.div>
        )}
        {phase === "result" && (
          <motion.div
            key="result"
            initial={{ opacity: 0, scale: 0.7 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: "spring", duration: 0.6 }}
            className="text-center py-2"
          >
            <div className="relative mx-auto w-56 h-56 flex items-center justify-center">
              <span className="absolute inset-0 rarefig-burst bg-[#14366B] scale-110" />
              <span className="absolute inset-0 rarefig-burst bg-[#FFD23F]" />
              <span className="relative text-center">
                <span
                  className="block font-rarefig text-6xl text-[#C9342B]"
                  data-testid="grade-result"
                >
                  {fig.grade}
                </span>
                <span className="block mt-1 text-xs font-extrabold uppercase tracking-[0.2em] text-[#14366B]">
                  {fig.cond}
                </span>
              </span>
            </div>
            <p className="mt-4 text-lg font-bold text-[#3a5384]">{fig.note}</p>
            <p className="mt-1 text-xs font-extrabold uppercase tracking-[0.2em] text-[#14366B]/60">
              Rarefig certified — collectors know the difference
            </p>
            <button
              onClick={reset}
              data-testid="grade-reset"
              className="mt-6 px-8 py-3 bg-[#14366B] text-[#FFF6E3] font-extrabold uppercase tracking-[0.2em] text-sm border-4 border-[#14366B] hover:bg-[#FFF6E3] hover:text-[#14366B] transition-colors"
            >
              Scan another
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function CaseRarefig() {
  return (
    <main
      className="min-h-screen bg-[#FFF6E3] text-[#14366B]"
      data-testid="case-rarefig-page"
    >
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-[#FFD23F] border-b-4 border-[#14366B]">
        <div className="max-w-[1300px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <Link
            to="/#work"
            className="flex items-center gap-2 font-extrabold text-xs uppercase tracking-[0.15em] hover:text-[#C9342B] transition-colors"
            data-testid="rarefig-back-link"
          >
            <ArrowLeft className="w-4 h-4" /> All work
          </Link>
          <img
            src={LOGO}
            alt="Rarefig logo"
            className="h-10 md:h-12 w-auto"
            data-testid="rarefig-nav-logo"
          />
          <Link
            to="/work/kickz"
            className="hidden md:flex items-center gap-2 font-extrabold text-xs uppercase tracking-[0.15em] hover:text-[#C9342B] transition-colors"
            data-testid="rarefig-next-link"
          >
            Next project <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </header>

      {/* Hero — navy comic sunburst, logo centered */}
      <section className="relative overflow-hidden rarefig-hero-bg">
        <div className="relative z-10 max-w-[1300px] mx-auto px-6 md:px-10 pt-16 pb-16 md:pt-24 md:pb-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              {["Website", "E-commerce", "Collectibles", "2025"].map((t) => (
                <span
                  key={t}
                  className="px-4 py-1.5 bg-[#14366B] text-[#FFF6E3] text-xs font-extrabold uppercase tracking-[0.15em] border-2 border-[#14366B] shadow-[3px_3px_0_#C9342B] hover:-translate-y-0.5 transition-transform duration-300 cursor-default"
                >
                  {t}
                </span>
              ))}
            </div>
            <motion.img
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, delay: 0.15 }}
              whileHover={{ rotate: -2, scale: 1.04 }}
              src={LOGO}
              alt="Rarefig — collectible figures webshop"
              className="relative mx-auto w-[min(420px,68vw)] drop-shadow-[6px_8px_0_rgba(20,54,107,0.45)]"
              data-testid="rarefig-hero-logo"
            />
            <p className="mt-10 text-xl md:text-3xl font-extrabold uppercase tracking-[0.1em] text-[#14366B]">
              Collectors know the difference.
            </p>
          </motion.div>
        </div>

        {/* Meta band */}
        <div className="relative z-10 bg-[#56B7CE] border-y-4 border-[#14366B] py-8">
          <div className="max-w-[1300px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-6 text-[#FFF6E3]">
            {[
              ["Role", "UI/UX, Brand & Illustration"],
              ["Year", "2025"],
              ["Platform", "Responsive Web"],
              ["Deliverables", "Webshop, mascot, identity"],
            ].map(([k, v]) => (
              <div key={k}>
                <div className="text-[10px] uppercase tracking-[0.2em] font-extrabold opacity-80">
                  {k}
                </div>
                <div className="mt-1 font-extrabold text-sm md:text-base">
                  {v}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lego divider */}
      <LegoDivider />

      {/* Laptop mockup */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-[1300px] mx-auto px-6 md:px-10">
          <motion.img
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            src={LAPTOP}
            alt="Rarefig website on laptop"
            className="w-full border-4 border-[#14366B] shadow-[12px_12px_0_#14366B]"
            data-testid="rarefig-mockup-image"
          />
        </div>
      </section>

      {/* Brief */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-[1300px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <span className="inline-block px-4 py-1.5 bg-[#C9342B] text-[#FFF6E3] text-xs font-extrabold uppercase tracking-[0.15em]">
              The Brief
            </span>
          </div>
          <div className="md:col-span-8">
            <h2 className="text-3xl md:text-5xl font-extrabold uppercase leading-tight tracking-wide">
              A comic-shop feeling,{" "}
              <span className="text-[#C9342B]">online.</span>
            </h2>
            <p className="mt-6 text-lg md:text-xl leading-relaxed text-[#3a5384]">
              Rarefig is a webshop for collectible figures — Funko Pops,
              minifigures, prop replicas and statues. The goal: bottle the joy
              of walking into a neighborhood comic shop and pour it into a
              modern e-commerce experience, complete with a grading service
              that gives collectors real confidence in what they buy.
            </p>
          </div>
        </div>
      </section>

      {/* The Name */}
      <section className="bg-[#14366B] text-[#FFF6E3] py-20 md:py-28">
        <div className="max-w-[1300px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <span className="inline-block px-4 py-1.5 bg-[#FFD23F] text-[#14366B] text-xs font-extrabold uppercase tracking-[0.15em]">
              The Name
            </span>
            <h2 className="mt-6 text-3xl md:text-5xl font-extrabold uppercase leading-tight tracking-wide">
              Rare <span className="text-[#FFD23F]">+</span> Fig
            </h2>
            <div className="mt-8 space-y-6">
              <div
                className="border-4 border-[#FFD23F] p-6"
                data-testid="rarefig-name-rare"
              >
                <span className="font-rarefig text-2xl text-[#FFD23F]">
                  Rare
                </span>
                <p className="mt-2 leading-relaxed text-[#FFF6E3]/85">
                  Highlights the exclusivity, uniqueness and value of
                  collectible items — speaking directly to the collector's
                  passion for finding treasures that are hard to come by.
                </p>
              </div>
              <div
                className="border-4 border-[#56B7CE] p-6"
                data-testid="rarefig-name-fig"
              >
                <span className="font-rarefig text-2xl text-[#56B7CE]">
                  Fig
                </span>
                <p className="mt-2 leading-relaxed text-[#FFF6E3]/85">
                  Short for <em>figure</em> — clearly communicating the brand's
                  focus on collectible figurines, from action figures to LEGO
                  and Funko.
                </p>
              </div>
            </div>
            <p className="mt-8 text-lg leading-relaxed text-[#FFF6E3]/85">
              Together, <strong className="text-[#FFD23F]">Rarefig</strong>{" "}
              establishes a strong, memorable identity. Short, distinctive and
              meaningful — it instantly resonates with collectors who know the
              thrill of discovering something rare and valuable.
            </p>
          </div>
          <div className="md:col-span-6">
            <img
              src={LOCKUP}
              alt="Rarefig brand lockup with mascot dragon"
              className="w-full border-4 border-[#FFD23F] shadow-[12px_12px_0_#FFD23F]"
              data-testid="rarefig-lockup-image"
            />
          </div>
        </div>
      </section>

      {/* Inspiration band */}
      <section className="bg-[#FFD23F] border-y-4 border-[#14366B] py-20 md:py-28">
        <div className="max-w-[1300px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 items-center">
          <div className="md:col-span-6">
            <span className="inline-block px-4 py-1.5 bg-[#14366B] text-[#FFF6E3] text-xs font-extrabold uppercase tracking-[0.15em]">
              Inspiration
            </span>
            <h2 className="mt-6 text-3xl md:text-5xl font-extrabold uppercase leading-tight tracking-wide">
              Saturday-morning cartoons & vintage toy aisles.
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-[#14366B]/80">
              The visual world borrows from pulp comic covers, retro toy
              packaging and hand-painted shop signs. Groovy display lettering,
              thick navy outlines and a sun-faded primary palette make every
              page feel like a collectible itself.
            </p>
            <p className="mt-4 text-lg leading-relaxed text-[#14366B]/80">
              At the center: a mascot dragon hoarding figures like treasure —
              equal parts shop owner and fellow collector — who guides users
              from the hero shelf to the newsletter sign-off.
            </p>
          </div>
          <div className="md:col-span-6">
            <div className="border-4 border-[#14366B] shadow-[12px_12px_0_#14366B] bg-[#FFF6E3] max-h-[560px] overflow-y-auto rarefig-scroll">
              <img
                src={FULLPAGE}
                alt="Rarefig shop page — full design"
                className="w-full"
                data-testid="rarefig-fullpage-image"
              />
            </div>
            <p className="mt-3 text-xs font-extrabold uppercase tracking-[0.2em] text-[#14366B]/70 text-center">
              ↑ Scroll the frame to tour the full shop page
            </p>
          </div>
          <div className="md:col-span-12">
            <img
              src={SHOP_SCENE}
              alt="Rarefig brand world — the shop scene illustration"
              className="w-full border-4 border-[#14366B] shadow-[12px_12px_0_#14366B]"
              data-testid="rarefig-shopscene-image"
            />
            <p className="mt-3 text-xs font-extrabold uppercase tracking-[0.2em] text-[#14366B]/70 text-center">
              The brand world — mascot, shelves and grails
            </p>
          </div>
        </div>
      </section>

      {/* In Motion — grading prototype on a MacBook */}
      <section className="relative bg-[#56B7CE] border-b-4 border-[#14366B] py-20 md:py-28 overflow-hidden rarefig-halftone-light">
        <div className="relative max-w-[1300px] mx-auto px-6 md:px-10">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 bg-[#C9342B] text-[#FFF6E3] text-xs font-extrabold uppercase tracking-[0.15em]">
              In Motion — Grading Prototype
            </span>
            <h2 className="mt-6 text-3xl md:text-5xl font-extrabold uppercase leading-tight tracking-wide text-[#FFF6E3]">
              Upload. Scan. <span className="text-[#FFD23F]">Graded.</span>
            </h2>
            <p className="mt-5 text-lg leading-relaxed text-[#FFF6E3]/90 max-w-[58ch] mx-auto">
              The grading service prototype in action: upload a photo of your
              collectible, the site scans it and returns a condition grade —
              authentication and peace of mind in one flow.
            </p>
          </div>
          {/* MacBook frame */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="max-w-[860px] mx-auto"
            data-testid="rarefig-macbook"
          >
            <div className="rounded-t-2xl border-[12px] border-[#1d1d1f] border-b-0 bg-black overflow-hidden shadow-[0_30px_60px_rgba(20,54,107,0.45)]">
              <video
                src={SCAN_VIDEO}
                autoPlay
                loop
                muted
                playsInline
                className="w-full h-auto block"
                data-testid="rarefig-demo-video"
              />
            </div>
            <div className="relative h-5 md:h-6 bg-[#2f2f31] rounded-b-2xl mx-[-5%] shadow-[0_12px_24px_rgba(20,54,107,0.35)]">
              <span className="absolute left-1/2 top-0 -translate-x-1/2 w-24 h-2.5 bg-[#1d1d1f] rounded-b-xl" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Try it — interactive grading demo */}
      <section className="relative py-20 md:py-28 rarefig-halftone overflow-hidden">
        <div className="relative max-w-[1300px] mx-auto px-6 md:px-10">
          <div className="text-center mb-14">
            <span className="inline-block px-4 py-1.5 bg-[#FFD23F] text-[#14366B] border-2 border-[#14366B] text-xs font-extrabold uppercase tracking-[0.15em]">
              Try it yourself
            </span>
            <h2 className="mt-6 text-3xl md:text-5xl font-extrabold uppercase leading-tight tracking-wide">
              A tiny taste of the{" "}
              <span className="text-[#C9342B]">real grading flow.</span>
            </h2>
          </div>
          <div className="relative">
            <GradingDemo />
            <motion.img
              initial={{ opacity: 0, x: 40, rotate: 0 }}
              whileInView={{ opacity: 1, x: 0, rotate: 8 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              src={SCANNER}
              alt=""
              aria-hidden="true"
              className="hidden lg:block absolute -right-12 xl:-right-20 -bottom-10 w-44 xl:w-52 drop-shadow-[6px_8px_0_rgba(20,54,107,0.25)] pointer-events-none"
              data-testid="rarefig-scanner-illustration"
            />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-[#FFF6E3] border-t-4 border-[#14366B] py-20 md:py-28">
        <div className="max-w-[1300px] mx-auto px-6 md:px-10">
          <span className="inline-block px-4 py-1.5 bg-[#56B7CE] text-[#FFF6E3] text-xs font-extrabold uppercase tracking-[0.15em]">
            How it works
          </span>
          <h2 className="mt-6 text-3xl md:text-5xl font-extrabold uppercase leading-tight tracking-wide max-w-[22ch]">
            Built for the hunt, graded for trust.
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((f, i) => (
              <motion.div
                key={f.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                whileHover={{ rotate: i % 2 ? 1 : -1, y: -4 }}
                className="relative bg-[#FFF6E3] border-4 border-[#14366B] p-8 shadow-[8px_8px_0_#14366B]"
                data-testid={`rarefig-feature-${i}`}
              >
                <div className="absolute -top-4 left-6 px-3 py-1 bg-[#C9342B] text-[#FFF6E3] text-[10px] font-extrabold uppercase tracking-[0.2em]">
                  Feature {String(i + 1).padStart(2, "0")}
                </div>
                <div
                  className="w-14 h-14 border-4 border-[#14366B] flex items-center justify-center"
                  style={{ background: f.bg }}
                >
                  <f.icon className="w-7 h-7" style={{ color: f.iconColor }} />
                </div>
                <h3 className="mt-5 text-2xl font-extrabold uppercase tracking-wide">
                  {f.title}
                </h3>
                <p className="mt-3 text-[#3a5384] leading-relaxed">{f.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Design language */}
      <section className="bg-[#14366B] text-[#FFF6E3] py-20 md:py-28">
        <div className="max-w-[1300px] mx-auto px-6 md:px-10">
          <span className="inline-block px-4 py-1.5 bg-[#FFD23F] text-[#14366B] text-xs font-extrabold uppercase tracking-[0.15em]">
            Design Language
          </span>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-10">
            <div className="md:col-span-7">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {palette.map((c) => (
                  <motion.div
                    key={c.hex}
                    whileHover={{ scale: 1.05, rotate: -1.5 }}
                    className="border-4 border-[#FFF6E3] p-5 h-32 flex flex-col justify-between"
                    style={{ background: c.hex, color: c.text }}
                    data-testid={`rarefig-swatch-${c.name.replace(/\s/g, "-").toLowerCase()}`}
                  >
                    <span className="font-extrabold text-sm uppercase tracking-wide">
                      {c.name}
                    </span>
                    <span className="font-mono text-xs opacity-80">
                      {c.hex}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
            <div className="md:col-span-5 bg-[#FFF6E3] text-[#14366B] border-4 border-[#FFD23F] p-8">
              <div className="font-rarefig text-[72px] leading-none text-[#C9342B]">
                Aa
              </div>
              <div className="mt-4 font-extrabold text-xl uppercase tracking-wide">
                Groovy meets grid.
              </div>
              <p className="mt-3 text-[#3a5384] leading-relaxed">
                A psychedelic display face for the brand voice, paired with a
                disciplined bold-caps system font for navigation, filters and
                product cards — playful on the surface, rigorous underneath.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Lego divider 2 */}
      <LegoDivider />

      {/* Footer / next */}
      <footer className="bg-[#C9342B] text-[#FFF6E3] py-16 border-t-4 border-[#14366B]">
        <div className="max-w-[1300px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <Link
            to="/#work"
            className="flex items-center gap-2 font-extrabold uppercase tracking-[0.15em] text-xs hover:text-[#FFD23F] transition-colors"
            data-testid="rarefig-footer-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back to all work
          </Link>
          <Link
            to="/work/kickz"
            className="group flex items-center gap-4"
            data-testid="rarefig-footer-next"
          >
            <span className="text-2xl md:text-4xl font-extrabold uppercase tracking-wide group-hover:text-[#FFD23F] transition-colors">
              Next: K!ckz
            </span>
            <ArrowRight className="w-8 h-8 transition-transform duration-300 group-hover:translate-x-2" />
          </Link>
        </div>
      </footer>
    </main>
  );
}
